import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import dayjs from "dayjs";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useOrderHistoryFilters } from "@/app/hooks/useOrderHistoryFilter";
import { OrderHistoryFilterFieldEnum } from "@/enums/orderHistoryFilterFiledEnum";
import { Option } from "@/app/interfaces/Option";
import dataService from "@/app/services/dataService";
import { StatusGroupIdsEnum } from "@/enums/statusGroupIdsEnum";
import {
  DEFAULT_PAGE_NUMBER,
  PAGINATION_PAGE_SIZE_OPTIONS,
} from "@/app/constants/constants";
import FetchLookupOptionsHelper from "@/app/helpers/FetchLookupOptionsHelper";
import { LookupTypesEnum } from "@/enums/lookupTypesEnum";
import FilterHelper from "@/app/helpers/FilterHelper";
import { StatusGroupEnum } from "@/enums/StatusGroupEnum";
import { ExportTypeEnum } from "@/enums/exportTypeEnum";
import DownloadHelper from "@/app/helpers/DownloadHelper";
import { adaptFileResponse } from "@/app/adapter/fileAdapter";
import PageLoadingOverlay from "../shared/pageLoadingOverlay";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import { useOrderHistoryTable } from "@/app/hooks/useOrderHistoryTable";
import SimpleModal from "../shared/simpleModal";
import { useLocation, useNavigate } from "react-router-dom";
import UrlHelper from "@/app/helpers/UrlHelper";
import { urlParamsEnum } from "@/enums/urlParamsEnum";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import OrderStatusButton from "./OrderStatusButton";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import {
  clearFromStorage,
  loadFromStorage,
  saveToStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";

const LOADING_MESSAGE = "We are preparing your file. Please wait.";
const DOWNLOAD_ERROR = "Failed to download file.";
const LIMIT_MESSAGE =
  "Downloads are limited to a maximum of 500 orders per download. Please refine your search and try again.";
const DOWNLOAD_LIMIT = 500;
export const STATUS_GROUPS: Record<
  Exclude<StatusGroupEnum, StatusGroupEnum.ALL>,
  Set<number>
> = {
  QUOTED: new Set([StatusGroupIdsEnum.QUOTED]),
  HOVER_QUOTED: new Set([StatusGroupIdsEnum.HOVER]),
  IN_PROGRESS: new Set([
    StatusGroupIdsEnum.ORDERED,
    StatusGroupIdsEnum.PICKED,
    StatusGroupIdsEnum.SHIPPED,
  ]),
  COMPLETED: new Set([
    StatusGroupIdsEnum.INVOICED,
    StatusGroupIdsEnum.PAID_IN_FULL,
  ]),
};

const MONTHS = [1, 3, 6, 12];

const OrderHeaderFilters = () => {
  const {
    updateMultipleFilters,
    updateFilter,
    filters,
    setActiveGroup,
    activeGroup,
  } = useOrderHistoryFilters();
  const { pagination, totalCount } = useOrderHistoryTable();
  const { setNotification } = useGlobalNotification();
  const { search } = useLocation();
  const initialTabRef = useRef<string | null>(
    UrlHelper.getQueryParam(search, urlParamsEnum.TAB)
  );

  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";
  const navigate = useNavigate();
  const location = useLocation();
  const hoverOrderEnabled = useSelector(
    (state: RootState) =>
      state.userState.user?.hasHoverRole || state.userState.user?.isAdmin
  );

  const [isSimpleModalOpen, setIsSimpleModalOpen] = useState<boolean>(false);
  const [numberOfMonths, setNumberOfMonths] = useState<null | number>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const exportsOpen = Boolean(exportAnchorEl);
  const openMonthsMenu = Boolean(anchorEl);

  const { data: statusesData, isLoading: statusesLoading } = useQuery({
    queryKey: [
      "order-statuses",
      {
        search: "",
        page: DEFAULT_PAGE_NUMBER,
        pageSize: PAGINATION_PAGE_SIZE_OPTIONS[0],
      },
    ],
    queryFn: ({ signal }) => {
      return FetchLookupOptionsHelper.fetchLookupOptions({
        lookupType: LookupTypesEnum.ORDER_STATUSES,
        search: "",
        page: DEFAULT_PAGE_NUMBER,
        pageSize: PAGINATION_PAGE_SIZE_OPTIONS[0],
        signal,
      });
    },
  });

  const statusOptions: Option[] = useMemo(() => {
    return statusesData?.items ?? [];
  }, [statusesData]);

  const payload = useMemo(() => {
    return FilterHelper.buildOrderHistoryPayload(filters, pagination);
  }, [filters, pagination]);

  const { data: statusCountData, isLoading: statusesCountLoading } = useQuery({
    queryKey: ["order-statuses-count", payload],
    queryFn: async ({ signal }) => {
      const result = await dataService.orderHistoryStatusCount({
        ...payload,
        signal,
      });

      const list = result?.data?.statusCounts ?? [];
      const totalCount = result?.data?.totalCount ?? 0;

      const totals = {
        ALL: totalCount,
        QUOTED: 0,
        IN_PROGRESS: 0,
        COMPLETED: 0,
        HOVER_QUOTED: 0,
      };

      for (const item of list) {
        const id = item.statusId;
        const count = item?.count ?? 0;

        if (STATUS_GROUPS.QUOTED.has(id)) totals.QUOTED += count;
        if (STATUS_GROUPS.IN_PROGRESS.has(id)) totals.IN_PROGRESS += count;
        if (STATUS_GROUPS.COMPLETED.has(id)) totals.COMPLETED += count;
        if (STATUS_GROUPS.HOVER_QUOTED.has(id)) totals.HOVER_QUOTED += count;
      }

      return totals;
    },
  });

  const onOrderStatusGroupChangeHandler = useCallback(
    (group: StatusGroupEnum, clearUlrParams: boolean = true) => {
      if (clearUlrParams) {
        UrlHelper.clearAllSearchParams(navigate, location);
        initialTabRef.current = null;
      }

      setActiveGroup(group);

      if (group === StatusGroupEnum.ALL) {
        updateFilter(OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS, []);
        updateFilter(OrderHistoryFilterFieldEnum.TYPE_IDS, []);
        return;
      }

      const selectedOptions = getGroupOptions(statusOptions, group);
      updateFilter(
        OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS,
        selectedOptions
      );
      updateFilter(OrderHistoryFilterFieldEnum.TYPE_IDS, []);

      if (group === StatusGroupEnum.QUOTED) {
        const quoteOption: Option[] = [
          { value: "Quote", text: "Quote", isFlagged: null },
        ];
        updateFilter(OrderHistoryFilterFieldEnum.TYPE_IDS, quoteOption);
      }

      if (group === StatusGroupEnum.HOVER_QUOTED) {
        const hoverQuoteOption: Option[] = [
          { value: "Hover", text: "Hover", isFlagged: null },
        ];
        updateFilter(OrderHistoryFilterFieldEnum.TYPE_IDS, hoverQuoteOption);
      }
    },
    [setActiveGroup, updateFilter, initialTabRef, statusOptions]
  );

  useEffect(() => {
    if (
      (initialTabRef.current === "quotes" && !statusesLoading) ||
      search.includes("tab=quotes")
    ) {
      setActiveGroup(StatusGroupEnum.QUOTED);
      onOrderStatusGroupChangeHandler(StatusGroupEnum.QUOTED, false);
    }

    if (
      (initialTabRef.current === "hoverQuotes" && !statusesLoading) ||
      search.includes("tab=hoverQuotes")
    ) {
      setActiveGroup(StatusGroupEnum.HOVER_QUOTED);
      onOrderStatusGroupChangeHandler(StatusGroupEnum.HOVER_QUOTED, false);
    }

    if (search.includes("tab=orders")) {
      setActiveGroup(StatusGroupEnum.ALL);
      onOrderStatusGroupChangeHandler(StatusGroupEnum.ALL, false);
    }

    const months = loadFromStorage<number>(
      userId,
      STORAGE_KEYS.ORDER_FILTERS_MONTHS
    );
    if (months) {
      setNumberOfMonths(months);
    }
  }, [statusesLoading, onOrderStatusGroupChangeHandler, search]);

  useEffect(() => {
    if (numberOfMonths == null) return;
    const dates = calculateDates(numberOfMonths);
    const isStartSame = dayjs(dates.startDate).isSame(filters.startDate, "day");
    const isEndSame = dayjs(dates.endDate).isSame(filters.endDate, "day");
    if (!isStartSame || !isEndSame) {
      setNumberOfMonths(null);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    if (
      initialTabRef.current === "quotes" ||
      initialTabRef.current === "hoverQuotes"
    ) {
      return;
    }

    let selectedGroup = StatusGroupEnum.ALL;

    if (
      filters.typeIds.length > 0 &&
      filters.typeIds[0].value !== "Quote" &&
      filters.typeIds[0].value !== "Hover"
    ) {
      setActiveGroup(selectedGroup);
      return;
    }

    const rawIds = FilterHelper.extractIdFromOptions(filters.orderStatusIds);
    const ids = rawIds.map(Number);

    if (ids.length > 0) {
      for (const [groupName, groupSet] of Object.entries(STATUS_GROUPS)) {
        if (
          ids.length === groupSet.size &&
          ids.every((id) => groupSet.has(id))
        ) {
          selectedGroup = groupName as Exclude<
            StatusGroupEnum,
            StatusGroupEnum.ALL
          >;
          break;
        }
      }
    }

    if (
      selectedGroup === StatusGroupEnum.QUOTED &&
      filters.typeIds.length === 1 &&
      filters.typeIds[0].value === "Quote"
    ) {
      setActiveGroup((prev) => (prev === selectedGroup ? prev : selectedGroup));
      return;
    }
    if (filters.typeIds.length === 1 && filters.typeIds[0].value === "Hover") {
      selectedGroup = StatusGroupEnum.HOVER_QUOTED;
      setActiveGroup((prev) => (prev === selectedGroup ? prev : selectedGroup));
      return;
    }

    if (
      selectedGroup !== StatusGroupEnum.QUOTED &&
      selectedGroup !== StatusGroupEnum.HOVER_QUOTED &&
      filters.typeIds.length === 0
    ) {
      setActiveGroup((prev) => (prev === selectedGroup ? prev : selectedGroup));
      return;
    }

    setActiveGroup(StatusGroupEnum.ALL);
  }, [filters.orderStatusIds, filters.typeIds]);

  const calculateDates = (numberOfMonths: number) => {
    const endDate = dayjs();
    const startDate = endDate.subtract(numberOfMonths, "month");

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const getMonthsDisplayText = (months: number | null) => {
    if (months === null) {
      return StatusGroupEnum.ALL;
    }

    return months === 1 ? `${months} Month` : `${months} Months`;
  };

  const getGroupOptions = (
    items: Option[],
    group: StatusGroupEnum
  ): Option[] => {
    if (group === StatusGroupEnum.ALL) {
      return items;
    }

    const set = STATUS_GROUPS[group];

    return items.filter((option) => set.has(Number(option.value)));
  };

  const onMonthsMenuHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onMonthsMenuCloseHandler = () => {
    setAnchorEl(null);
  };

  const onMonthsChangeHandler = (months: number | null) => {
    UrlHelper.clearAllSearchParams(navigate, location);
    if (months === null) {
      setNumberOfMonths(null);
      clearFromStorage(userId, STORAGE_KEYS.ORDER_FILTERS_MONTHS);
      updateMultipleFilters({
        [OrderHistoryFilterFieldEnum.START_DATE]: null,
        [OrderHistoryFilterFieldEnum.END_DATE]: null,
      });
    } else {
      const dates = calculateDates(months);
      setNumberOfMonths(months);
      saveToStorage(userId, STORAGE_KEYS.ORDER_FILTERS_MONTHS, months);
      updateMultipleFilters({
        [OrderHistoryFilterFieldEnum.START_DATE]: dates.startDate,
        [OrderHistoryFilterFieldEnum.END_DATE]: dates.endDate,
      });
    }
    onMonthsMenuCloseHandler();
  };

  const handleExportButtonClick = (exportType: ExportTypeEnum) => {
    if (totalCount > DOWNLOAD_LIMIT) {
      setIsSimpleModalOpen(true);
      return;
    }

    exportToFile.mutate(exportType);
  };

  const handleExportPDFButtonClick = () => {
    if (totalCount > DOWNLOAD_LIMIT) {
      setIsSimpleModalOpen(true);

      return;
    }

    downloadClientPDF.mutate();
  };

  const exportToFile = useMutation({
    mutationFn: (exportType: ExportTypeEnum) =>
      dataService.exportToExcelRaw(payload, exportType),
    onSuccess: (res) => {
      const { blob, filename } = adaptFileResponse(res);
      DownloadHelper.triggerDownload(blob, filename);
    },
    onError: async () => {
      setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  const downloadClientPDF = useMutation({
    mutationFn: async () => {
      const result = await dataService.getExportOrdersDataForPdf(payload);

      if (result.success) {
        DownloadHelper.downloadOrdersPdf(result.data);
      } else {
        setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  function useCustomBreakpoint() {
    const [isCustomRange, setIsCustomRange] = useState(false);

    useEffect(() => {
      const mediaQuery = window.matchMedia(
        "(min-width: 768px) and (max-width: 1535px)"
      );
      setIsCustomRange(mediaQuery.matches);

      const listener = (e) => setIsCustomRange(e.matches);
      mediaQuery.addListener(listener);
      return () => mediaQuery.removeListener(listener);
    }, []);

    return isCustomRange;
  }

  const isCustomRange = useCustomBreakpoint();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", xl: "row" },
        alignItems: { xs: "center", lg: "center" },
        flexWrap: "wrap",
        gap: 2,
        mb: 2,
        p: 3,
      }}
    >
      {isCustomRange ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-around",
              mb: 1,
            }}
          >
            <Box
              sx={{
                minWidth: "340px",
                display: "flex",
                minHeight: "10px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{ fontWeight: "bold" }}
                >
                  {statusCountData ? statusCountData[activeGroup] : 0} Orders
                </Typography>
                <Typography variant="body1" component="span">
                  Placed in
                </Typography>
              </Box>
              <Button
                aria-controls={openMonthsMenu ? "months-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={openMonthsMenu ? "true" : undefined}
                onClick={onMonthsMenuHandler}
                variant="outlined"
                sx={{
                  display: "flex",
                  textTransform: "none",
                  fontWeight: 400,
                  minWidth: "112px",
                  alignItems: "center",
                  lineHeight: 0,
                  marginLeft: "5px",
                }}
              >
                {getMonthsDisplayText(numberOfMonths)}
                <Box component="span" sx={{ ml: 0.5 }}>
                  <ExpandMoreIcon />
                </Box>
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={openMonthsMenu}
                onClose={onMonthsMenuCloseHandler}
                MenuListProps={{ "aria-labelledby": "months-button" }}
              >
                {MONTHS.map((month) => (
                  <MenuItem
                    key={month}
                    onClick={() => onMonthsChangeHandler(month)}
                  >
                    {month} Month{month > 1 ? "s" : ""}
                  </MenuItem>
                ))}
                <MenuItem onClick={() => onMonthsChangeHandler(null)}>
                  ALL
                </MenuItem>
              </Menu>
            </Box>
            <Box
              sx={{
                mt: { xs: 2, sm: 0 },
                minWidth: "340px",
                display: "flex",
                justifyContent: { xs: "center", xl: "end" },
                marginLeft: 0,
              }}
            >
              <Button
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                sx={{
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#62B5E5",
                  textAlign: { xs: "left", sm: "right" },
                  textTransform: "capitalize",
                }}
              >
                Exports & Downloads
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={exportsOpen}
                onClose={() => setExportAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExportButtonClick(
                      ExportTypeEnum.EXPORT_ORDER_DETAILS_TO_EXCEL
                    );
                  }}
                  sx={{
                    color: "#62B5E5",
                    fontSize: "14px",
                    display: "flex !important",
                    alignItems: "center",

                    "& svg": {
                      fill: "#62B5E5",
                    },

                    "&:hover": {
                      color: "#2a5771",

                      "& svg": {
                        fill: "#2a5771",
                      },
                    },
                  }}
                >
                  <TableViewOutlinedIcon sx={{ mr: "5px" }} fontSize="small" />
                  Export Order Details to Excel
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExportPDFButtonClick();
                  }}
                  sx={{
                    color: "#62B5E5",
                    fontSize: "14px",
                    display: "flex !important",
                    alignItems: "center",

                    "& svg": {
                      fill: "#62B5E5",
                    },

                    "&:hover": {
                      color: "#2a5771",

                      "& svg": {
                        fill: "#2a5771",
                      },
                    },
                  }}
                >
                  <PictureAsPdfOutlinedIcon
                    sx={{ mr: "5px" }}
                    fontSize="small"
                  />
                  Download Order Details PDF
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExportButtonClick(
                      ExportTypeEnum.EXPORT_ORDER_ITEM_TO_EXCEL
                    );
                  }}
                  sx={{
                    color: "#62B5E5",
                    fontSize: "14px",
                    display: "flex !important",
                    alignItems: "center",

                    "& svg": {
                      fill: "#62B5E5",
                    },

                    "&:hover": {
                      color: "#2a5771",

                      "& svg": {
                        fill: "#2a5771",
                      },
                    },
                  }}
                >
                  <TableViewOutlinedIcon sx={{ mr: "5px" }} fontSize="small" />
                  Export Item Details to Excel
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <Box sx={{ width: "auto", m: "auto" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                flex: 1,
                mt: { xs: 1, sm: 0 },
              }}
            >
              {[
                { label: "All", group: StatusGroupEnum.ALL },
                { label: "In Progress", group: StatusGroupEnum.IN_PROGRESS },
                { label: "Completed", group: StatusGroupEnum.COMPLETED },
                { label: "Open Quotes", group: StatusGroupEnum.QUOTED },
              ].map(({ label, group }) => (
                <OrderStatusButton
                  key={group}
                  label={label}
                  group={group}
                  activeGroup={activeGroup}
                  onChange={onOrderStatusGroupChangeHandler}
                  statusCountData={statusCountData}
                  filters={filters}
                  statusesCountLoading={statusesCountLoading}
                />
              ))}
            </Box>
            {hoverOrderEnabled && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <OrderStatusButton
                  label="Hover Quotes"
                  group={StatusGroupEnum.HOVER_QUOTED}
                  activeGroup={activeGroup}
                  onChange={onOrderStatusGroupChangeHandler}
                  statusCountData={statusCountData}
                  filters={filters}
                  statusesCountLoading={statusesCountLoading}
                />
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              minWidth: { xs: "100%", sm: "340px" },
              display: "flex",
              justifyContent: { xs: "center", sm: "start" },
              minHeight: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                {statusCountData ? statusCountData[activeGroup] : 0} Orders
              </Typography>
              <Typography variant="body1" component="span">
                Placed in
              </Typography>
            </Box>
            <Button
              aria-controls={openMonthsMenu ? "months-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openMonthsMenu ? "true" : undefined}
              onClick={onMonthsMenuHandler}
              variant="outlined"
              sx={{
                display: "flex",
                textTransform: "none",
                fontWeight: 400,
                minWidth: "112px",
                alignItems: "center",
                lineHeight: 0,
                marginLeft: "5px",
              }}
            >
              {getMonthsDisplayText(numberOfMonths)}
              <Box component="span" sx={{ ml: 0.5 }}>
                <ExpandMoreIcon />
              </Box>
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={openMonthsMenu}
              onClose={onMonthsMenuCloseHandler}
              MenuListProps={{ "aria-labelledby": "months-button" }}
            >
              {MONTHS.map((month) => (
                <MenuItem
                  key={month}
                  onClick={() => onMonthsChangeHandler(month)}
                >
                  {month} Month{month > 1 ? "s" : ""}
                </MenuItem>
              ))}
              <MenuItem onClick={() => onMonthsChangeHandler(null)}>
                ALL
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ width: "auto", m: "auto" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                flex: 1,
                mt: { xs: 1, sm: 0 },
              }}
            >
              {[
                { label: "All", group: StatusGroupEnum.ALL },
                { label: "In Progress", group: StatusGroupEnum.IN_PROGRESS },
                { label: "Completed", group: StatusGroupEnum.COMPLETED },
                { label: "Open Quotes", group: StatusGroupEnum.QUOTED },
              ].map(({ label, group }) => (
                <OrderStatusButton
                  key={group}
                  label={label}
                  group={group}
                  activeGroup={activeGroup}
                  onChange={onOrderStatusGroupChangeHandler}
                  statusCountData={statusCountData}
                  filters={filters}
                  statusesCountLoading={statusesCountLoading}
                />
              ))}
            </Box>
            {hoverOrderEnabled && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <OrderStatusButton
                  label="Hover Quotes"
                  group={StatusGroupEnum.HOVER_QUOTED}
                  activeGroup={activeGroup}
                  onChange={onOrderStatusGroupChangeHandler}
                  statusCountData={statusCountData}
                  filters={filters}
                  statusesCountLoading={statusesCountLoading}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              mt: { xs: 2, sm: 0 },
              minWidth: { xs: "100%", md: "340px" },
              display: "flex",
              justifyContent: { xs: "center", xl: "end" },
              marginLeft: 0,
            }}
          >
            <Button
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                color: "#62B5E5",
                textAlign: { xs: "left", sm: "right" },
                textTransform: "capitalize",
              }}
            >
              Exports & Downloads
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportsOpen}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExportButtonClick(
                    ExportTypeEnum.EXPORT_ORDER_DETAILS_TO_EXCEL
                  );
                }}
                sx={{
                  color: "#62B5E5",
                  fontSize: "14px",
                  display: "flex !important",
                  alignItems: "center",

                  "& svg": {
                    fill: "#62B5E5",
                  },

                  "&:hover": {
                    color: "#2a5771",

                    "& svg": {
                      fill: "#2a5771",
                    },
                  },
                }}
              >
                <TableViewOutlinedIcon sx={{ mr: "5px" }} fontSize="small" />
                Export Order Details to Excel
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExportPDFButtonClick();
                }}
                sx={{
                  color: "#62B5E5",
                  fontSize: "14px",
                  display: "flex !important",
                  alignItems: "center",

                  "& svg": {
                    fill: "#62B5E5",
                  },

                  "&:hover": {
                    color: "#2a5771",

                    "& svg": {
                      fill: "#2a5771",
                    },
                  },
                }}
              >
                <PictureAsPdfOutlinedIcon sx={{ mr: "5px" }} fontSize="small" />
                Download Order Details PDF
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExportButtonClick(
                    ExportTypeEnum.EXPORT_ORDER_ITEM_TO_EXCEL
                  );
                }}
                sx={{
                  color: "#62B5E5",
                  fontSize: "14px",
                  display: "flex !important",
                  alignItems: "center",

                  "& svg": {
                    fill: "#62B5E5",
                  },

                  "&:hover": {
                    color: "#2a5771",

                    "& svg": {
                      fill: "#2a5771",
                    },
                  },
                }}
              >
                <TableViewOutlinedIcon sx={{ mr: "5px" }} fontSize="small" />
                Export Item Details to Excel
              </MenuItem>
            </Menu>
          </Box>
        </>
      )}

      {(exportToFile.isPending || downloadClientPDF.isPending) && (
        <PageLoadingOverlay text={LOADING_MESSAGE} />
      )}
      <SimpleModal
        isOpen={isSimpleModalOpen}
        message={LIMIT_MESSAGE}
        onClose={() => setIsSimpleModalOpen(false)}
      />
    </Box>
  );
};

export default OrderHeaderFilters;
